import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, Loader2, MailCheck } from 'lucide-react';
import { Turnstile } from '@marsidev/react-turnstile';
import { useAuth, type AuthModalMode } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import type { Session } from '../../api';
import {
  ApiError,
  confirmPasswordReset,
  requestPasswordReset,
  sendEmailCode,
  verifyEmailCode,
  type OAuthProvider,
} from '../../api';
import PhoneField from '../PhoneField';
import OAuthButtons from './OAuthButtons';

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

const inputCls =
  'bg-field text-fg placeholder:text-fg-subtle mt-1 block w-full rounded-xl border border-strong px-4 py-2.5 text-sm shadow-sm outline-none focus:border-brand-600 focus:ring-1 focus:ring-brand-600';
const labelCls = 'block text-sm font-medium text-fg-secondary';
const primaryBtn =
  'flex w-full items-center justify-center gap-2 rounded-xl bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:opacity-50';

function errMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message;
  return fallback;
}

/**
 * Where a freshly-authenticated user should land, based on their role and where
 * they logged in from. Returns null to stay on the current page — used when a
 * customer logs in mid-booking on a salon page, so they can finish the booking
 * instead of being yanked to their bookings list.
 */
function postLoginPath(session: Session, pathname: string): string | null {
  if (session.role === 'owner') return '/admin';
  if (pathname.startsWith('/shop/')) return null;
  return '/account/bookings';
}

/* ─────────────────────────  Login  ───────────────────────── */

function LoginForm() {
  const { login, loginWithOAuth, closeAuth, setAuthMode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const redirectAfterAuth = (session: Session) => {
    closeAuth();
    const dest = postLoginPath(session, location.pathname);
    if (dest) navigate(dest);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      redirectAfterAuth(await login(identifier.trim(), password));
    } catch (err) {
      setError(errMessage(err, 'Invalid credentials.'));
    } finally {
      setBusy(false);
    }
  };

  const onOAuth = async (provider: OAuthProvider, token: string) => {
    setError('');
    try {
      redirectAfterAuth(await loginWithOAuth(provider, token));
    } catch (err) {
      setError(errMessage(err, 'Sign-in failed.'));
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-fg-muted text-sm">Log in to book appointments and leave reviews.</p>
      {error && <div className="bg-danger-soft text-danger rounded-xl p-3 text-sm">{error}</div>}
      <div>
        <label className={labelCls} htmlFor="login-id">
          Email
        </label>
        <input
          id="login-id"
          type="email"
          className={inputCls}
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          required
          autoComplete="email"
        />
      </div>
      <div>
        <label className={labelCls} htmlFor="login-pw">
          Password
        </label>
        <input
          id="login-pw"
          type="password"
          className={inputCls}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="current-password"
        />
      </div>
      <button type="submit" className={primaryBtn} disabled={busy}>
        {busy && <Loader2 className="h-4 w-4 animate-spin" />}
        Log in
      </button>
      <OAuthButtons onToken={onOAuth} />
      <div className="flex justify-between text-sm">
        <button
          type="button"
          className="text-accent hover:underline"
          onClick={() => setAuthMode('reset')}
        >
          Forgot password?
        </button>
        <button
          type="button"
          className="text-accent hover:underline"
          onClick={() => setAuthMode('register')}
        >
          Create an account
        </button>
      </div>
      <div className="border-subtle text-fg-muted border-t pt-3 text-center text-sm">
        Salon owner?{' '}
        <button
          type="button"
          className="text-accent font-medium hover:underline"
          onClick={() => {
            closeAuth();
            navigate('/admin');
          }}
        >
          Go to your dashboard →
        </button>
      </div>
    </form>
  );
}

/* ─────────────────────────  Register  ───────────────────────── */

function RegisterForm() {
  const { register, loginWithOAuth, closeAuth, setAuthMode } = useAuth();
  const { resolved } = useTheme();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState<string | undefined>();
  const [password, setPassword] = useState('');
  const [captcha, setCaptcha] = useState<string | null>(TURNSTILE_SITE_KEY ? null : 'dev');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!phone) {
      setError('A phone number is required.');
      return;
    }
    if (!captcha) {
      setError('Please complete the captcha.');
      return;
    }
    setBusy(true);
    try {
      await register({
        name: name.trim(),
        email: email.trim(),
        phone,
        password,
        captchaToken: captcha,
      });
      setAuthMode('verify');
    } catch (err) {
      setError(errMessage(err, 'Could not create your account.'));
      setCaptcha(TURNSTILE_SITE_KEY ? null : 'dev');
    } finally {
      setBusy(false);
    }
  };

  const onOAuth = async (provider: OAuthProvider, token: string) => {
    setError('');
    try {
      await loginWithOAuth(provider, token);
      closeAuth();
    } catch (err) {
      setError(errMessage(err, 'Sign-in failed.'));
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-fg-muted text-sm">Create an account to book your appointment.</p>
      {error && <div className="bg-danger-soft text-danger rounded-xl p-3 text-sm">{error}</div>}
      <div>
        <label className={labelCls} htmlFor="reg-name">
          Full name
        </label>
        <input
          id="reg-name"
          className={inputCls}
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
      </div>
      <div>
        <label className={labelCls} htmlFor="reg-email">
          Email
        </label>
        <input
          id="reg-email"
          type="email"
          className={inputCls}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
        />
      </div>
      <div>
        <label className={labelCls} htmlFor="reg-phone">
          Phone number
        </label>
        <PhoneField
          id="reg-phone"
          value={phone}
          onChange={setPhone}
          className="mt-1"
          selectClassName="rounded-xl border border-strong shadow-sm px-3 py-2.5 text-sm"
          inputClassName="flex-1 rounded-xl border border-strong shadow-sm px-4 py-2.5 text-sm outline-none focus:border-brand-600 focus:ring-1 focus:ring-brand-600"
        />
      </div>
      <div>
        <label className={labelCls} htmlFor="reg-pw">
          Password
        </label>
        <input
          id="reg-pw"
          type="password"
          className={inputCls}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="new-password"
          minLength={8}
        />
      </div>
      {TURNSTILE_SITE_KEY && (
        <div className="flex justify-center">
          {/* `resolved`, not 'auto': auto follows the OS, which would show a
              dark widget to someone who forced Light. */}
          <Turnstile
            key={resolved}
            siteKey={TURNSTILE_SITE_KEY}
            onSuccess={setCaptcha}
            onExpire={() => setCaptcha(null)}
            onError={() => setCaptcha(null)}
            options={{ theme: resolved }}
          />
        </div>
      )}
      <button type="submit" className={primaryBtn} disabled={busy}>
        {busy && <Loader2 className="h-4 w-4 animate-spin" />}
        Create account
      </button>
      <OAuthButtons onToken={onOAuth} />
      <div className="text-center text-sm">
        <button
          type="button"
          className="text-accent hover:underline"
          onClick={() => setAuthMode('login')}
        >
          Already have an account? Log in
        </button>
      </div>
    </form>
  );
}

/* ─────────────────────────  Email verification  ───────────────────────── */

function EmailVerificationForm() {
  const { session, setSession, closeAuth } = useAuth();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);
  const email = session?.customer?.email || session?.tenant?.name || 'your email';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const updated = await verifyEmailCode(code.trim());
      setSession(updated);
      closeAuth();
    } catch (err) {
      setError(errMessage(err, 'Invalid or expired code.'));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setError('');
    setInfo('');
    try {
      await sendEmailCode();
      setInfo('A new code has been sent.');
    } catch (err) {
      setError(errMessage(err, 'Could not send a new code.'));
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="flex flex-col items-center text-center">
        <MailCheck className="text-accent mb-2 h-10 w-10" />
        <p className="text-fg-muted text-sm">
          We sent a 6-digit code to <span className="text-fg font-medium">{email}</span>. Enter it
          below to verify your email.
        </p>
      </div>
      {error && <div className="bg-danger-soft text-danger rounded-xl p-3 text-sm">{error}</div>}
      {info && <div className="bg-success-soft text-success rounded-xl p-3 text-sm">{info}</div>}
      <div>
        <label className={labelCls} htmlFor="verify-code">
          Verification code
        </label>
        <input
          id="verify-code"
          className={`${inputCls} tracking-[0.3em]`}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          inputMode="numeric"
          placeholder="000000"
          required
        />
      </div>
      <button type="submit" className={primaryBtn} disabled={busy || code.length !== 6}>
        {busy && <Loader2 className="h-4 w-4 animate-spin" />}
        Verify email
      </button>
      <div className="text-center text-sm">
        <button type="button" className="text-accent hover:underline" onClick={resend}>
          Resend code
        </button>
      </div>
    </form>
  );
}

/* ─────────────────────────  Password reset  ───────────────────────── */

function PasswordResetForm() {
  const { setAuthMode } = useAuth();
  const [step, setStep] = useState<'request' | 'confirm'>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);

  const requestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await requestPasswordReset(email.trim());
      setInfo('If that email is registered, a reset code has been sent.');
      setStep('confirm');
    } catch (err) {
      setError(errMessage(err, 'Something went wrong.'));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await confirmPasswordReset(email.trim(), code.trim(), newPassword);
      setInfo('Password reset. You can now log in.');
      setStep('request');
      setAuthMode('login');
    } catch (err) {
      setError(errMessage(err, 'Invalid or expired code.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={step === 'request' ? requestCode : confirm} className="space-y-4">
      <p className="text-fg-muted text-sm">
        {step === 'request'
          ? 'Enter your email and we will send you a reset code.'
          : 'Enter the code we emailed you and choose a new password.'}
      </p>
      {error && <div className="bg-danger-soft text-danger rounded-xl p-3 text-sm">{error}</div>}
      {info && <div className="bg-success-soft text-success rounded-xl p-3 text-sm">{info}</div>}
      <div>
        <label className={labelCls} htmlFor="reset-email">
          Email
        </label>
        <input
          id="reset-email"
          type="email"
          className={inputCls}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={step === 'confirm'}
        />
      </div>
      {step === 'confirm' && (
        <>
          <div>
            <label className={labelCls} htmlFor="reset-code">
              Reset code
            </label>
            <input
              id="reset-code"
              className={`${inputCls} tracking-[0.3em]`}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              placeholder="000000"
              required
            />
          </div>
          <div>
            <label className={labelCls} htmlFor="reset-pw">
              New password
            </label>
            <input
              id="reset-pw"
              type="password"
              className={inputCls}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className={labelCls} htmlFor="reset-pw-confirm">
              Confirm new password
            </label>
            <input
              id="reset-pw-confirm"
              type="password"
              className={inputCls}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
            />
          </div>
          <p className="text-fg-muted text-xs">
            Didn&rsquo;t receive the code? Contact{' '}
            <a href="mailto:support@calendyz.com" className="text-accent hover:underline">
              support@calendyz.com
            </a>
            .
          </p>
        </>
      )}
      <button type="submit" className={primaryBtn} disabled={busy}>
        {busy && <Loader2 className="h-4 w-4 animate-spin" />}
        {step === 'request' ? 'Send reset code' : 'Reset password'}
      </button>
      <div className="text-center text-sm">
        <button
          type="button"
          className="text-accent hover:underline"
          onClick={() => setAuthMode('login')}
        >
          Back to log in
        </button>
      </div>
    </form>
  );
}

/* ─────────────────────────  Modal shell  ───────────────────────── */

const TITLES: Record<AuthModalMode, string> = {
  login: 'Log in',
  register: 'Create account',
  verify: 'Verify your email',
  reset: 'Reset password',
};

export default function AuthModal() {
  const { authModalOpen, authModalMode, closeAuth } = useAuth();

  useEffect(() => {
    if (!authModalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeAuth();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [authModalOpen, closeAuth]);

  if (!authModalOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/50 p-4 py-10"
      onClick={closeAuth}
    >
      <div
        className="bg-surface relative w-full max-w-md rounded-2xl p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-fg text-xl font-semibold">{TITLES[authModalMode]}</h2>
          <button
            onClick={closeAuth}
            aria-label="Close"
            className="text-fg-subtle hover:bg-muted hover:text-fg-secondary rounded-full p-1 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {authModalMode === 'login' && <LoginForm />}
        {authModalMode === 'register' && <RegisterForm />}
        {authModalMode === 'verify' && <EmailVerificationForm />}
        {authModalMode === 'reset' && <PasswordResetForm />}
      </div>
    </div>
  );
}
